import React, {
  FC,
  useEffect,
  memo,
  useCallback,
  useState,
  useRef,
} from 'react';
import { getActions, getGlobal, withGlobal } from '../../global';

import {
  AnimationLevel,
  ERoutes,
  LeftColumnContent,
  MiddleColumnContent,
  SettingsScreens,
} from '../../types';
import type {
  ApiAttachBot,
  ApiAttachment,
  ApiChat,
  ApiMessage,
  ApiUser,
} from '../../api/types';
import type {
  ApiLimitTypeWithModal,
  GlobalState,
  TabState,
} from '../../global/types';

import '../../global/actions/all';
import { BASE_EMOJI_KEYWORD_LANG, DEBUG } from '../../config';
import { IS_ANDROID, IS_ELECTRON } from '../../util/windowEnvironment';
import {
  selectChatMessage,
  selectTabState,
  selectCurrentMessageList,
  selectIsCurrentUserPremium,
  selectIsForwardModalOpen,
  selectIsMediaViewerOpen,
  selectIsRightColumnShown,
  selectIsServiceChatReady,
  selectUser,
  selectIsReactionPickerOpen,
  selectCurrentChat,
} from '../../global/selectors';
import { waitForTransitionEnd } from '../../util/cssAnimationEndListeners';
import { processDeepLink } from '../../util/deeplink';
import { parseInitialLocationHash } from '../../util/routing';
import { fastRaf } from '../../util/schedulers';
import { Bundles, loadBundle } from '../../util/moduleLoader';

import useEffectWithPrevDeps from '../../hooks/useEffectWithPrevDeps';
import useBackgroundMode from '../../hooks/useBackgroundMode';
import useBeforeUnload from '../../hooks/useBeforeUnload';
import useSyncEffect from '../../hooks/useSyncEffect';
import usePreventPinchZoomGesture from '../../hooks/usePreventPinchZoomGesture';
import useForceUpdate from '../../hooks/useForceUpdate';
import { dispatchHeavyAnimationEvent } from '../../hooks/useHeavyAnimationCheck';
import useInterval from '../../hooks/useInterval';
import useAppLayout from '../../hooks/useAppLayout';
import useTimeout from '../../hooks/useTimeout';

import LeftColumn from '../left/LeftColumn';
import useShowTransition from '../../hooks/useShowTransition';
import MiddleColumn from '../middle/MiddleColumn';
import RightColumn from '../right/RightColumn';
import ForwardRecipientPicker from './ForwardRecipientPicker';
import HistoryCalendar from './HistoryCalendar';
import UnreadCount from '../common/UnreadCounter';

import MediaViewer from '../mediaViewer/MediaViewer';
import NewContactModal from './NewContactModal';
import ActiveCallHeader from '../calls/ActiveCallHeader';
import PhoneCall from '../calls/phone/PhoneCall';
import GroupCall from '../calls/group/GroupCall';
import RatePhoneCallModal from '../calls/phone/RatePhoneCallModal';
import TwoFaEmailCodeModal from '../middle/settings/twoFa/TwoFaEmailCodeModal';
import Dialogs from './Dialogs';
import classNames from 'classnames';
import DownloadManager from './DownloadManager';
import DraftRecipientPicker from './DraftRecipientPicker';
import AudioPlayer from '../middle/AudioPlayer';
import PremiumLimitReachedModal from './premium/common/PremiumLimitReachedModal';
import { ElectronEvent } from '../../types/electron';
import SafeLinkModal from './SafeLinkModal';
import ConfettiContainer from './ConfettiContainer';
import SnapEffectContainer from './visualEffects/SnapEffectContainer';
import AiPromptNatifications from './AiPromptNatifications';
import ReactionPicker from '../middle/message/ReactionPicker';
import { Navigate, useNavigate, useParams } from 'react-router';
import { Helmet } from 'react-helmet';
import useBrowserOnline from '../../hooks/useBrowserOnline';
import './Main.scss';
import ShareRecipientPicker from './ShareRecipientPicker';

export enum FeedLeftList {
  ForYou,
  Following,
  Explore,
}

export enum FeedMiddleList {
  Main,
  Settings,
}

type StateProps = {
  isMasterTab?: boolean;
  lastSyncTime?: number;
  isMiddleColumnOpen: boolean;
  isRightColumnOpen: boolean;
  isMediaViewerOpen: boolean;
  isForwardModalOpen: boolean;
  hasNotifications: boolean;
  hasDialogs: boolean;
  audioMessage?: ApiMessage;
  safeLinkModalUrl?: string;
  isHistoryCalendarOpen: boolean;
  shouldSkipHistoryAnimations?: boolean;
  openedStickerSetShortName?: string;
  openedCustomEmojiSetIds?: string[];
  activeGroupCallId?: string;
  isServiceChatReady?: boolean;
  animationLevel: AnimationLevel;
  wasTimeFormatSetManually?: boolean;
  isPhoneCallActive?: boolean;
  addedSetIds?: string[];
  addedCustomEmojiIds?: string[];
  newContactUserId?: string;
  newContactByPhoneNumber?: boolean;
  openedGame?: TabState['openedGame'];
  gameTitle?: string;
  isRatePhoneCallModalOpen?: boolean;
  webApp?: TabState['webApp'];
  isPremiumModalOpen?: boolean;
  botTrustRequest?: TabState['botTrustRequest'];
  botTrustRequestBot?: ApiUser;
  attachBotToInstall?: ApiAttachBot;
  requestedAttachBotInChat?: TabState['requestedAttachBotInChat'];
  requestedDraft?: TabState['requestedDraft'];
  requestedShare: TabState['requestedShare'];
  currentUser?: ApiUser;
  urlAuth?: TabState['urlAuth'];
  limitReached?: ApiLimitTypeWithModal;
  limitFile?: ApiAttachment;
  deleteFolderDialogId?: number;
  isPaymentModalOpen?: boolean;
  isReceiptModalOpen?: boolean;
  isCurrentUserPremium?: boolean;
  middleScreen: MiddleColumnContent;
  leftScreen: LeftColumnContent;
  isLeftColumnOpen?: boolean;
  isReactionPickerOpen: boolean;
  authState: GlobalState['authState'];
  currentChat?: ApiChat;
};

const APP_OUTDATED_TIMEOUT_MS = 5 * 60 * 1000; // 5 min
const CALL_BUNDLE_LOADING_DELAY_MS = 5000; // 5 sec

// eslint-disable-next-line @typescript-eslint/naming-convention
let DEBUG_isLogged = false;

const Main: FC<StateProps> = ({
  lastSyncTime,
  isLeftColumnOpen,
  isMiddleColumnOpen,
  isRightColumnOpen,
  isMediaViewerOpen,
  isForwardModalOpen,
  hasDialogs,
  isHistoryCalendarOpen,
  shouldSkipHistoryAnimations,
  isServiceChatReady,
  animationLevel,
  wasTimeFormatSetManually,
  addedSetIds,
  addedCustomEmojiIds,
  newContactUserId,
  newContactByPhoneNumber,
  isCurrentUserPremium,
  isMasterTab,
  activeGroupCallId,
  isPhoneCallActive,
  isRatePhoneCallModalOpen,
  requestedDraft,
  requestedShare,
  audioMessage,
  limitReached,
  limitFile,
  safeLinkModalUrl,
  isReactionPickerOpen,
  authState,
  currentChat,
  middleScreen,
}) => {
  const {
    initMain,
    loadAnimatedEmojis,
    loadNotificationSettings,
    loadNotificationExceptions,
    updateIsOnline,
    onTabFocusChange,
    loadTopInlineBots,
    loadEmojiKeywords,
    loadAvailableReactions,
    loadStickerSets,
    loadPremiumGifts,
    loadDefaultTopicIcons,
    loadAddedStickers,
    loadFavoriteStickers,
    loadDefaultStatusIcons,
    ensureTimeFormat,
    closeStickerSetModal,
    closeCustomEmojiSets,
    checkVersionNotification,
    loadConfig,
    loadAppConfig,
    loadAttachBots,
    loadContactList,
    loadCustomEmojis,
    loadGenericEmojiEffects,
    checkAppVersion,
    toggleLeftColumn,
    loadRecentEmojiStatuses,
    loadAuthorizations,
    getBotList,
    getPriceAi,
    setAiPurchases,
    setIsElectronUpdateAvailable,
    getWallets,
    init,
    openChat,
  } = getActions();

  if (DEBUG && !DEBUG_isLogged) {
    DEBUG_isLogged = true;
    console.log('>>> RENDER MAIN');
  }

  const { isTablet, isMobile, isDesktop } = useAppLayout();

  const [feedLeftScreen, setFeedLeftScreen] = useState(FeedLeftList.Following);
  const [feedMiddleScreen, setFeedMiddleScreen] = useState(FeedMiddleList.Main);
  const [title, setTitle] = useState('');

  const [support, setsupport] = useState<SettingsScreens | null>(null);
  const [openCall, setOpenCall] = useState<string>('');

  const { page } = useParams();

  const handleCalls = (name: string) => {
    setOpenCall(name);
  };

  // Preload Calls bundle to initialize sounds for iOS
  useTimeout(() => {
    void loadBundle(Bundles.Calls);
  }, CALL_BUNDLE_LOADING_DELAY_MS);

  useEffect(() => {
    if (!isLeftColumnOpen && !isMiddleColumnOpen && !isDesktop) {
      // Always display at least one column
      toggleLeftColumn();
    } else if (isLeftColumnOpen && isMiddleColumnOpen && isMobile) {
      // Can't have two active columns at the same time
      //toggleLeftColumn();
    }
  }, [
    isDesktop,
    isLeftColumnOpen,
    isMiddleColumnOpen,
    isMobile,
    toggleLeftColumn,
  ]);

  useInterval(
    checkAppVersion,
    isMasterTab ? APP_OUTDATED_TIMEOUT_MS : undefined,
    true
  );

  useEffect(() => {
    if (!IS_ELECTRON) {
      return undefined;
    }

    const removeUpdateAvailableListener = window.electron!.on(
      ElectronEvent.UPDATE_AVAILABLE,
      () => {
        setIsElectronUpdateAvailable(true);
      }
    );

    const removeUpdateErrorListener = window.electron!.on(
      ElectronEvent.UPDATE_ERROR,
      () => {
        setIsElectronUpdateAvailable(false);
        removeUpdateAvailableListener?.();
      }
    );

    return () => {
      removeUpdateErrorListener?.();
      removeUpdateAvailableListener?.();
    };
  }, []);

  // Initial API calls
  useEffect(() => {
    if (lastSyncTime && isMasterTab) {
      updateIsOnline(true);
      loadConfig();
      loadAppConfig();
      initMain();
      loadAvailableReactions();
      loadAnimatedEmojis();
      loadGenericEmojiEffects();
      loadNotificationSettings();
      loadNotificationExceptions();
      loadTopInlineBots();
      loadEmojiKeywords({ language: BASE_EMOJI_KEYWORD_LANG });
      loadAttachBots();
      loadContactList();
      loadPremiumGifts();
      loadDefaultTopicIcons();
      checkAppVersion();
      loadAuthorizations();
      getBotList();
      getPriceAi();
      setAiPurchases();
      getWallets();
    }
  }, [lastSyncTime, isMasterTab]);

  useEffect(() => {
    if (isMasterTab && isCurrentUserPremium) {
      loadDefaultStatusIcons();
      loadRecentEmojiStatuses();
    }
  }, [isCurrentUserPremium, isMasterTab]);

  const isBrowserOnline = useBrowserOnline();

  useEffectWithPrevDeps(
    ([prevbrowserOnline]) => {
      if (isBrowserOnline && !prevbrowserOnline && lastSyncTime) {
        init();
        if (currentChat && middleScreen === MiddleColumnContent.Messages) {
          openChat({ id: currentChat.id });
        }
      }
    },
    [isBrowserOnline, currentChat, middleScreen, lastSyncTime]
  );

  // Re-fetch cached saved emoji for `localDb`
  useEffectWithPrevDeps(
    ([prevLastSyncTime]) => {
      if (!prevLastSyncTime && lastSyncTime && isMasterTab) {
        loadCustomEmojis({
          ids: Object.keys(getGlobal().customEmojis.byId),
          ignoreCache: true,
        });
      }
    },
    [lastSyncTime, isMasterTab, loadCustomEmojis]
  );

  // Sticker sets
  useEffect(() => {
    if (lastSyncTime && isMasterTab) {
      if (!addedSetIds || !addedCustomEmojiIds) {
        loadStickerSets();
        loadFavoriteStickers();
      }

      if (addedSetIds && addedCustomEmojiIds) {
        loadAddedStickers();
      }
    }
  }, [
    lastSyncTime,
    addedSetIds,
    loadStickerSets,
    loadFavoriteStickers,
    loadAddedStickers,
    addedCustomEmojiIds,
    isMasterTab,
  ]);

  // Check version when service chat is ready
  useEffect(() => {
    if (lastSyncTime && isServiceChatReady && isMasterTab) {
      checkVersionNotification();
    }
  }, [lastSyncTime, isServiceChatReady, checkVersionNotification, isMasterTab]);

  // Ensure time format
  useEffect(() => {
    if (lastSyncTime && !wasTimeFormatSetManually) {
      ensureTimeFormat();
    }
  }, [lastSyncTime, wasTimeFormatSetManually, ensureTimeFormat]);

  // Parse deep link //
  useEffect(() => {
    if (lastSyncTime) {
      const parsedInitialLocationHash = parseInitialLocationHash();

      if (parsedInitialLocationHash?.elloaddr) {
        processDeepLink(
          decodeURIComponent(parsedInitialLocationHash.elloaddr),
          true
        );
      }
    }
  }, [lastSyncTime]);

  useEffect(() => {
    if (!page) return;
    let title;
    switch (page) {
      case ERoutes.feed:
        title = 'Feed';
        break;
      case ERoutes.profile:
        title = 'Profile';
        break;
      case ERoutes.contacts:
        title = 'Contacts';
        break;
      case ERoutes.aiSpace:
        title = 'Ai Space';
        break;
      case ERoutes.elloPay:
        title = 'Ello Pay';
        break;
      default:
        title = '';
    }
    setTitle(title);
  }, [page]);

  const willAnimateLeftColumnRef = useRef(false);
  const forceUpdate = useForceUpdate();

  const leftColumnTransition = useShowTransition(
    isLeftColumnOpen,
    undefined,
    true,
    undefined,
    shouldSkipHistoryAnimations,
    undefined,
    true
  );
  // Handle opening middle column
  useSyncEffect(
    ([prevIsLeftColumnOpen]) => {
      if (
        prevIsLeftColumnOpen === undefined ||
        isLeftColumnOpen === prevIsLeftColumnOpen ||
        animationLevel === 0
      ) {
        return;
      }

      willAnimateLeftColumnRef.current = true;

      if (IS_ANDROID) {
        fastRaf(() => {
          document.body.classList.toggle(
            'android-left-blackout-open',
            !isLeftColumnOpen
          );
        });
      }

      const dispatchHeavyAnimationEnd = dispatchHeavyAnimationEvent();

      waitForTransitionEnd(document.getElementById('MiddleColumn')!, () => {
        dispatchHeavyAnimationEnd();
        willAnimateLeftColumnRef.current = false;
        forceUpdate();
      });
    },
    [isLeftColumnOpen, animationLevel, forceUpdate]
  );

  const rightColumnTransition = useShowTransition(
    isRightColumnOpen,
    undefined,
    true,
    undefined,
    shouldSkipHistoryAnimations,
    undefined,
    true
  );
  const willAnimateRightColumnRef = useRef(false);
  const [isNarrowMessageList, setIsNarrowMessageList] =
    useState(isRightColumnOpen);

  // Handle opening right column
  useSyncEffect(
    ([prevIsRightColumnOpen]) => {
      if (
        prevIsRightColumnOpen === undefined ||
        isRightColumnOpen === prevIsRightColumnOpen
      ) {
        return;
      }

      if (animationLevel === 0) {
        setIsNarrowMessageList(isRightColumnOpen);
        return;
      }

      willAnimateRightColumnRef.current = true;

      const dispatchHeavyAnimationEnd = dispatchHeavyAnimationEvent();

      waitForTransitionEnd(document.getElementById('RightColumn')!, () => {
        dispatchHeavyAnimationEnd();
        willAnimateRightColumnRef.current = false;
        forceUpdate();
        setIsNarrowMessageList(isRightColumnOpen);
      });
    },
    [isRightColumnOpen, animationLevel, forceUpdate]
  );

  const className = classNames({
    'left-column-shown': leftColumnTransition.hasShownClass,
    'left-column-open': leftColumnTransition.hasOpenClass,
    'left-column-animating': willAnimateLeftColumnRef.current,
    'right-column-shown': rightColumnTransition.hasShownClass,
    'right-column-open': rightColumnTransition.hasOpenClass,
    'right-column-animating': willAnimateRightColumnRef.current,
    'narrow-message-list': isNarrowMessageList,
    'history-animation-disabled': shouldSkipHistoryAnimations,
    'suport-content': support === SettingsScreens.Support,
  });

  const handleBlur = useCallback(() => {
    onTabFocusChange({ isBlurred: true });
  }, [onTabFocusChange]);

  const handleFocus = useCallback(() => {
    onTabFocusChange({ isBlurred: false });
  }, [onTabFocusChange]);

  const handleStickerSetModalClose = useCallback(() => {
    closeStickerSetModal();
  }, [closeStickerSetModal]);

  const handleCustomEmojiSetsModalClose = useCallback(() => {
    closeCustomEmojiSets();
  }, [closeCustomEmojiSets]);

  // Online status and browser tab indicators
  useBackgroundMode(handleBlur, handleFocus);
  useBeforeUnload(handleBlur);
  usePreventPinchZoomGesture(isMediaViewerOpen);

  if (authState !== 'authorizationStateReady')
    return <Navigate to='/auth' replace />;

  return (
    <div id='Main' className={className}>
      <>
        <Helmet title={title} />
        <LeftColumn
          handleCalls={handleCalls}
          openCall={openCall}
          setsupport={setsupport}
          isMobile={isMobile}
          isLeftShown={isLeftColumnOpen!}
          feedLeftScreen={feedLeftScreen}
          feedMiddleScreen={feedMiddleScreen}
          setFeedLeftScreen={setFeedLeftScreen}
          setFeedMiddleScreen={setFeedMiddleScreen}
        />
        <MiddleColumn
          isMobile={isMobile}
          handleCalls={handleCalls}
          feedMiddleScreen={feedMiddleScreen}
          setFeedMiddleScreen={setFeedMiddleScreen}
        />
        <RightColumn
          isMobile={isMobile!}
          isTablet={isTablet!}
          isLeftShown={isLeftColumnOpen!}
          lastSyncTime={lastSyncTime}
        />
        <MediaViewer isOpen={isMediaViewerOpen} />
        <ForwardRecipientPicker isOpen={isForwardModalOpen} />
        <Dialogs isOpen={hasDialogs} />
        <HistoryCalendar isOpen={isHistoryCalendarOpen} />

        <NewContactModal
          isOpen={Boolean(newContactUserId || newContactByPhoneNumber)}
          userId={newContactUserId}
          isByPhoneNumber={newContactByPhoneNumber}
        />
        <ConfettiContainer />
        <SnapEffectContainer />
        {activeGroupCallId && <GroupCall groupCallId={activeGroupCallId} />}
        <ActiveCallHeader
          isActive={Boolean(activeGroupCallId || isPhoneCallActive)}
        />
        <PhoneCall isActive={isPhoneCallActive} />
        <RatePhoneCallModal isOpen={isRatePhoneCallModalOpen} />

        <TwoFaEmailCodeModal />
        <DownloadManager />
        <DraftRecipientPicker requestedDraft={requestedDraft} />
        <ShareRecipientPicker requestedShare={requestedShare} />
        {audioMessage && (
          <AudioPlayer key={audioMessage.id} message={audioMessage} noUi />
        )}
        {limitReached && (
          <PremiumLimitReachedModal limit={limitReached} file={limitFile} />
        )}
        <UnreadCount isForAppBadge />
        <SafeLinkModal url={safeLinkModalUrl} />
        <AiPromptNatifications />
        <ReactionPicker isOpen={isReactionPickerOpen} />

        {/* <UrlAuthModal urlAuth={urlAuth} currentUser={currentUser} />*/}
        {/* <MessageListHistoryHandler /> */}
        {/*<StickerSetModal
        isOpen={Boolean(openedStickerSetShortName)}
        onClose={handleStickerSetModalClose}
        stickerSetShortName={openedStickerSetShortName}
      />
      <CustomEmojiSetsModal
        customEmojiSetIds={openedCustomEmojiSetIds}
        onClose={handleCustomEmojiSetsModalClose}
      />
     
      <GameModal openedGame={openedGame} gameTitle={gameTitle} />
      <WebAppModal webApp={webApp} />
      <BotTrustModal bot={botTrustRequestBot} type={botTrustRequest?.type} />
      <AttachBotInstallModal bot={attachBotToInstall} />
      <AttachBotRecipientPicker
        requestedAttachBotInChat={requestedAttachBotInChat}
      />
      
      {isPremiumModalOpen && <PremiumMainModal isOpen={isPremiumModalOpen} />}
      
      <PaymentModal isOpen={isPaymentModalOpen} onClose={closePaymentModal} />
      <ReceiptModal isOpen={isReceiptModalOpen} onClose={clearReceipt} />
      <DeleteFolderDialog deleteFolderDialogId={deleteFolderDialogId} /> */}
      </>
    </div>
  );
};

export default memo(
  withGlobal((global, { isMobile }): StateProps => {
    const {
      settings: {
        byKey: { animationLevel, wasTimeFormatSetManually },
      },
      lastSyncTime,
      authState,
    } = global;

    const {
      botTrustRequest,
      requestedAttachBotInstall,
      requestedAttachBotInChat,
      requestedDraft,
      requestedShare,
      urlAuth,
      webApp,
      safeLinkModalUrl,
      openedStickerSetShortName,
      openedCustomEmojiSetIds,
      shouldSkipHistoryAnimations,
      openedGame,
      audioPlayer,
      historyCalendarSelectedAt,
      notifications,
      dialogs,
      newContact,
      ratingPhoneCall,
      premiumModal,
      isMasterTab,
      payment,
      limitReachedModal,
      deleteFolderDialogModal,
      middleScreen,
      leftScreen,
    } = selectTabState(global);

    const { chatId: audioChatId, messageId: audioMessageId } = audioPlayer;
    const audioMessage =
      audioChatId && audioMessageId
        ? selectChatMessage(global, audioChatId, audioMessageId)
        : undefined;
    const gameMessage =
      openedGame &&
      selectChatMessage(global, openedGame.chatId, openedGame.messageId);
    const gameTitle = gameMessage?.content.game?.title;
    const currentUser = global.currentUserId
      ? selectUser(global, global.currentUserId)
      : undefined;
    const { chatId } = selectCurrentMessageList(global) || {};
    const currentChat = selectCurrentChat(global);

    return {
      lastSyncTime,
      isMiddleColumnOpen: Boolean(
        chatId ||
          middleScreen === MiddleColumnContent.QuickLinks ||
          [LeftColumnContent.Feed, LeftColumnContent.Settings].includes(
            leftScreen
          )
      ),
      isRightColumnOpen: selectIsRightColumnShown(global, isMobile),
      isMediaViewerOpen: selectIsMediaViewerOpen(global),
      isForwardModalOpen: selectIsForwardModalOpen(global),
      hasNotifications: Boolean(notifications.length),
      hasDialogs: Boolean(dialogs.length),
      audioMessage,
      safeLinkModalUrl,
      isHistoryCalendarOpen: Boolean(historyCalendarSelectedAt),
      shouldSkipHistoryAnimations,
      openedStickerSetShortName,
      openedCustomEmojiSetIds,
      isServiceChatReady: selectIsServiceChatReady(global),
      activeGroupCallId: isMasterTab
        ? global.groupCalls.activeGroupCallId
        : undefined,
      animationLevel,
      wasTimeFormatSetManually,
      isPhoneCallActive: isMasterTab ? Boolean(global.phoneCall) : undefined,
      addedSetIds: global.stickers.added.setIds,
      addedCustomEmojiIds: global.customEmojis.added.setIds,
      newContactUserId: newContact?.userId,
      newContactByPhoneNumber: newContact?.isByPhoneNumber,
      openedGame,
      gameTitle,
      isRatePhoneCallModalOpen: Boolean(ratingPhoneCall),
      botTrustRequest,
      botTrustRequestBot:
        botTrustRequest && selectUser(global, botTrustRequest.botId),
      attachBotToInstall: requestedAttachBotInstall?.bot,
      requestedAttachBotInChat,
      webApp,
      currentUser,
      urlAuth,
      isCurrentUserPremium: selectIsCurrentUserPremium(global),
      isPremiumModalOpen: premiumModal?.isOpen,
      limitReached: limitReachedModal?.limit,
      limitFile: limitReachedModal?.file,
      isPaymentModalOpen: payment.isPaymentModalOpen,
      isReceiptModalOpen: Boolean(payment.receipt),
      deleteFolderDialogId: deleteFolderDialogModal,
      isMasterTab,
      requestedDraft,
      requestedShare,
      middleScreen,
      leftScreen,
      isLeftColumnOpen: selectTabState(global).isLeftColumnShown,
      isReactionPickerOpen: selectIsReactionPickerOpen(global),
      authState,
      currentChat,
    };
  })(Main)
);
